import React, { createContext, useContext, useMemo } from 'react'
import { useAye } from '../hooks/useAye'
import { usePortalHook } from '../hooks/usePortalHook'
import { GuardedReturn } from '../utils/useGuardedCallback'

// Aye Types
import { DetectFaceResponse, RegisterCredentialsResponse } from '../core'

// Portal Types
import { DeleteAccountMethodRequestResponse } from '../core/api/http-methods/delete-account'
import { DeletePaymentMethodRequestResponse } from '../core/api/http-methods/delete-payment-method'
import { GetOtpResponse } from '../core/api/http-methods/get-otp'
import { GetPaymentMethodsResponse, PaymentConsents } from '../core/api/http-methods/get-payment-methods'
import { GetUserTransactionsResponse } from '../core/api/http-methods/get-user-transactions'
import { LoginPortalResponse } from '../core/api/http-methods/login'
import { PayWithoutPINResponse } from '../core/api/http-methods/pay-without-pin'
import { ResetPINResponse } from '../core/api/http-methods/reset-pin'
import { SaveUserResponse } from '../core/api/http-methods/save-user-info'
import { SetDefaultResponse } from '../core/api/http-methods/set-default'
import { UserI } from '../types/user'

// create context
const AppContext = createContext<
  | {
      // Aye Context
      detectedFace: string | undefined
      setDetectedFace: React.Dispatch<React.SetStateAction<string | undefined>>
      face?: DetectFaceResponse
      setUserPin: React.Dispatch<React.SetStateAction<number[]>>
      consent?: RegisterCredentialsResponse
      // verifyConsent: (
      //   token: string,
      // ) => Promise<GuardedReturn<VerifyConsentResponse>>
      setFace: React.Dispatch<
        React.SetStateAction<DetectFaceResponse | undefined>
      >
      setConsent: React.Dispatch<
        React.SetStateAction<RegisterCredentialsResponse | undefined>
      >
      userPin: number[]
      loading: boolean
      selectedOptions: { option_1: number; option_2: number } | undefined
      setSelectedOptions: React.Dispatch<
        React.SetStateAction<
          | {
              option_1: number
              option_2: number
            }
          | undefined
        >
      >

      // Portal Context
      getCode: (
        phoneNumber: string,
        eventType: string,
      ) => Promise<GuardedReturn<GetOtpResponse>>
      login: (
        phoneNumber: string,
        code: string,
      ) => Promise<GuardedReturn<LoginPortalResponse>>
      token: string | undefined
      user: UserI | undefined
      logout: () => void
      saveUser: (args_0: {
        user_name: string
        user_email: string
        user_birthday: string
        newsletter: boolean
        text_message: boolean
        marketing_and_offers: boolean
      }) => Promise<GuardedReturn<SaveUserResponse | undefined>>
      // addPaymentMethod: (
      //   args_0: AddPaymentMethodRequest['body'],
      // ) => Promise<GuardedReturn<AddPaymentMethodResponse | undefined>>
      deletePaymentMethod: (args_0: {
        phoneNumber: string
        consentId: string
      }) => Promise<
        GuardedReturn<DeletePaymentMethodRequestResponse | undefined>
      >
      transactions: (
        phoneNumber: string,
      ) => Promise<GetUserTransactionsResponse | undefined>
      paymentMethods: () => Promise<
      GetPaymentMethodsResponse['payment_consents'] | undefined
      >
      // verifyConsentPortal: (
      //   token: string,
      // ) => Promise<GuardedReturn<VerifyConsentResponse | undefined>>
      setDefault: (
        consentId: string,
      ) => Promise<GuardedReturn<SetDefaultResponse | undefined>>
      // updatePIN: (
      //   oldPin: string,
      //   newPin: string,
      //   transactionLimit: number,
      // ) => Promise<GuardedReturn<UpdatePINResponse | undefined>>
      // updateFace: (
      //   face_index: string,
      // ) => Promise<GuardedReturn<UpdateFaceResponse | undefined>>
      resetPIN: (
        newPin: string,
        otp: string,
        phoneNumber: string,
      ) => Promise<GuardedReturn<ResetPINResponse | undefined>>
      deleteAccount: (
        phoneNumber: string,
        otp: string,
      ) => Promise<
        GuardedReturn<DeleteAccountMethodRequestResponse | undefined>
      >
      payWithOutPin: (
        scanAndPayWithoutPin: boolean,
        transactionLimit: number,
        phoneNumber: string,
        pin: string | number,
      ) => Promise<GuardedReturn<PayWithoutPINResponse | undefined>>
    }
  | undefined
>(undefined)

const AyeProvider = ({ children }: any) => {
  const {
    detectedFace,
    setDetectedFace,
    face,
    setUserPin,
    consent,
    setFace,
    setConsent,
    userPin,
    loading: ayeLoading,
    selectedOptions,
    // verifyConsent,
    setSelectedOptions,
  } = useAye()

  const {
    getCode,
    login,
    token,
    user,
    loading: portalLoading,
    logout,
    saveUser,
    // addPaymentMethod,
    deletePaymentMethod,
    transactions,
    paymentMethods,
    // verifyConsent: verifyConsentPortal,
    setDefault,
    // updatePIN,
    resetPIN,
    deleteAccount,
    payWithOutPin,
    // updateFace,
  } = usePortalHook()

  const contextValue = useMemo(
    () => ({
      // Aye Values
      detectedFace,
      setDetectedFace,
      face,
      setUserPin,
      consent,
      setFace,
      setConsent,
      userPin,
      loading: ayeLoading || portalLoading, // combine both loading states
      selectedOptions,
      // verifyConsent,
      setSelectedOptions,

      // Portal Values
      payWithOutPin,
      deleteAccount,
      getCode,
      login,
      token,
      user,
      logout,
      saveUser,
      // addPaymentMethod,
      deletePaymentMethod,
      transactions,
      paymentMethods,
      // verifyConsentPortal,
      setDefault,
      // updatePIN,
      // updateFace,
      resetPIN,
    }),
    [
      detectedFace,
      face,
      setDetectedFace,
      setUserPin,
      consent,
      setFace,
      setConsent,
      userPin,
      ayeLoading,
      portalLoading,
      selectedOptions,
      // verifyConsent,
      setSelectedOptions,
      getCode,
      login,
      token,
      user,
      logout,
      saveUser,
      // addPaymentMethod,
      deletePaymentMethod,
      transactions,
      paymentMethods,
      // verifyConsentPortal,
      setDefault,
      // updatePIN,
      resetPIN,
      payWithOutPin,
      deleteAccount,
    ],
  )

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  )
}

// context consumer hook
const useApp = () => {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useApp was used outside of its Provider')
  }
  return context
}

export { AyeProvider, useApp }

